import { computed, ref } from 'vue'

export const COOKIE_CONSENT_STORAGE_KEY = 'achiraya_cookie_consent'
export const COOKIE_CONSENT_VERSION = 1

export interface CookieConsentCategories {
  essential: true
  analytics: boolean
  marketing: boolean
}

export interface CookieConsentState {
  version: number
  acceptedAt: string
  categories: CookieConsentCategories
}

const defaultCategories = (): CookieConsentCategories => ({
  essential: true,
  analytics: false,
  marketing: false,
})

const storedConsent = ref<CookieConsentState | null>(null)
const isSettingsOpen = ref(false)

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const normalizeConsent = (value: unknown): CookieConsentState | null => {
  if (!value || typeof value !== 'object') return null

  const candidate = value as Partial<CookieConsentState>
  if (candidate.version !== COOKIE_CONSENT_VERSION || typeof candidate.acceptedAt !== 'string') {
    return null
  }

  const categories = candidate.categories
  if (!categories || typeof categories !== 'object') return null

  return {
    version: COOKIE_CONSENT_VERSION,
    acceptedAt: candidate.acceptedAt,
    categories: {
      essential: true,
      analytics: Boolean(categories.analytics),
      marketing: Boolean(categories.marketing),
    },
  }
}

const readStoredConsent = (): CookieConsentState | null => {
  if (!isBrowser()) return null

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)
    if (!raw) return null

    return normalizeConsent(JSON.parse(raw))
  } catch {
    return null
  }
}

const persistConsent = (categories: CookieConsentCategories): CookieConsentState => {
  const consent: CookieConsentState = {
    version: COOKIE_CONSENT_VERSION,
    acceptedAt: new Date().toISOString(),
    categories: {
      essential: true,
      analytics: categories.analytics,
      marketing: categories.marketing,
    },
  }

  storedConsent.value = consent

  if (isBrowser()) {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent))
  }

  return consent
}

export const loadCookieConsent = () => {
  storedConsent.value = readStoredConsent()
  return storedConsent.value
}

export const useCookieConsent = () => {
  if (storedConsent.value === null) {
    loadCookieConsent()
  }

  const hasConsent = computed(() => storedConsent.value !== null)
  const categories = computed(() => storedConsent.value?.categories || defaultCategories())
  const hasAnalyticsConsent = computed(() => categories.value.analytics)
  const hasMarketingConsent = computed(() => categories.value.marketing)

  const acceptAll = () => persistConsent({
    essential: true,
    analytics: true,
    marketing: true,
  })

  const rejectOptional = () => persistConsent(defaultCategories())

  const savePreferences = (preferences: Pick<CookieConsentCategories, 'analytics' | 'marketing'>) => {
    return persistConsent({
      essential: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    })
  }

  const openSettings = () => {
    isSettingsOpen.value = true
  }

  const closeSettings = () => {
    isSettingsOpen.value = false
  }

  const resetCookieConsentForTest = () => {
    storedConsent.value = null
    isSettingsOpen.value = false
    if (isBrowser()) {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
    }
  }

  return {
    consent: storedConsent,
    categories,
    hasConsent,
    hasAnalyticsConsent,
    hasMarketingConsent,
    isSettingsOpen,
    acceptAll,
    rejectOptional,
    savePreferences,
    openSettings,
    closeSettings,
    loadCookieConsent,
    resetCookieConsentForTest,
  }
}
